import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';

interface Props {
  scrolled?: boolean;
}

export default function LanguageSwitcher({ scrolled = true }: Props) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'ja', label: '日本語', flag: '🇯🇵' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textColor = scrolled
    ? 'text-[#555555] hover:text-[#7C3AED]'
    : 'text-white/90 hover:text-white';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-300 cursor-pointer whitespace-nowrap ${textColor}`}
        style={{ fontSize: '14px', fontWeight: 500 }}
        aria-label="Language selector"
      >
        <span>{currentLanguage.flag}</span>
        <span>{currentLanguage.label}</span>
        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl overflow-hidden z-50 min-w-[150px]"
          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                currentLanguage.code === lang.code
                  ? 'bg-violet-50 text-[#7C3AED] font-medium'
                  : 'text-[#555555] hover:bg-gray-50 hover:text-[#7C3AED]'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
